import React from 'react'
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { radius, spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { LoadingPlaceholder } from '@mobi/component-library/Misc/LoadingPlaceholder'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { trackInfinityScrollEvent } from '@core/Services/Analytics/helpers/trackers'
import { getAccountTransactions } from '../helpers/api'

export const useAccountTransactionsData = ({ selectedDate, enabled = true }: Args) => {
  const queryKey = queryKeys.accountTransactions(selectedDate)

  const {
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    data,
    refetch,
    isSuccess,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam: nextPage }) => getAccountTransactions({ nextPage, selectedDate }),
    initialPageParam: '',
    getNextPageParam: prevPage => {
      if (prevPage.isFinalPage) return undefined
      return prevPage._nextPage || null
    },
    staleTime: 1_000 * 60 * 2, // 2 mins
    // Ensure fresh data on entry, rather than invalidate in all locations where bets are placed
    refetchOnMount: 'always',
    enabled: enabled,
  })

  React.useEffect(() => {
    if (isSuccess) {
      handleInfinityScrollTracking(data)
    }
  }, [data, isSuccess])

  const isFinalPage = data && data.pages.length > 0 && data.pages[data.pages.length - 1].isFinalPage
  const isEmptyState = isFinalPage && data.pages[0].sortedIds.length === 0

  return {
    data: enabled ? data : undefined,
    isError,
    isLoading,
    isFetching,
    isEmptyState,
    isFinalPage,
    refetch: () => {
      queryClient.removeQueries({ queryKey })
      refetch()
    },
    /** Will auto fetch next page when in view */
    LoadMoreButton: () => (
      <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
    ),
    SkeletonLoader,
    fetchNextPage,
    isFetchingNextPage,
  }
}

// ===============
// Local Component
// ===============

const SkeletonLoader: React.FC = () => (
  <Flex flexDirection='column' gap={spacing.smx1}>
    {Array(9)
      .fill(null)
      .map((_, i) => (
        <LoadingPlaceholder key={i} width='100%' height='10rem' borderRadius={radius.lg} />
      ))}
  </Flex>
)

// =============
// Local Helpers
// =============

function handleInfinityScrollTracking(data: InfiniteData<Response, unknown>) {
  trackInfinityScrollEvent({ location: 'Debit / Credit', loadCount: data.pages.length })
}

// =====
// Types
// =====

type Args = { selectedDate?: string; enabled?: boolean }
type Response = Awaited<ReturnType<typeof getAccountTransactions>>
